/*
 * Copyright (c) Nexign, JSC, 2019-2023.
 */
/**
 * Styled components for Modal component
 *
 * @author Evgenij Tyan
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @author Evgeniy Naumov
 * @version 1.25.1
 * @since 1.0.0
 */

import styled from 'styled-components';

import type { IAttrStyled } from '@/main/types/globalTypes';
import { icons } from '@assets/index';
import { LinkContainerStyled } from '@components/common/LinkBlock/styled/LinkStyled';
import { colors, fonts } from '@theme/index';

export const FullScreenContainerStyled = styled.div.attrs({
    className: 'modal',
    tabIndex: 0,
})<{ visible: boolean }>`
    position: fixed;
    top: 0;
    left: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    visibility: ${(props) => props.visible ? 'visible' : 'hidden'};
    width: 100%;
    height: 100%;
    margin: 0;
    z-index: 500;
`;

export const ModalBackgroundStyled = styled.div.attrs({
    'className': 'modal__vertical-scroll',
})`
    background-color: rgba(51, 51, 51, 0.5);
    overflow: auto;
    display: flex;
    justify-content: center;
    width: 100%;
    height: 100vh;
    z-index: 1;
    padding: 50px 0;
`;

export const ModalContainerStyled = styled.div.attrs({
    'className': 'modal__body',
    'data-modal-body': 'modalBody',
})`
    position: relative;
    min-height: 100px;
    height: fit-content;
    margin: auto;
    background-color: ${colors.backgroundColorWhite};
    cursor: default;
    z-index: 21;
`;

export const CrossButtonStyled = styled.img.attrs({
    'className': 'modal__close-button',
    'data-modal-close-button': 'modalCloseButton',
})`
    position: absolute;
    top: 12px;
    right: 12px;
    width: 40px;
    height: 40px;
    cursor: pointer;
`;

export interface IModalPopupContainerStyled {
    width?: string;
}

export interface IModalSeparatorStyled {
    margin: string;
};

export const ModalPopupTitleStyled = styled.div.attrs(({ className }: IAttrStyled) => ({
    'className': `modal-popup-title ${className || ''}`,
    'data-name': 'modal-popup-title',
}))<IAttrStyled>`
    ${fonts.textHeader3};

    .modal-popup-title__subtitle {
        ${fonts.textMain};
        overflow-wrap: break-word;

    }

    &.modal-popup-title__base {
        margin-bottom: 24px;
    }
`;

export const ModalPopupTextStyled = styled.div`
`;

export const ModalTitleStyled = styled.div`
    display: grid;
    grid-template-columns: max-content min-content;
    justify-content: space-between;
    align-items: center;
`;

export const ModalTitleTextStyled = styled.h3`
    margin: 0;

    &.title-text-icon {
        display: grid;
        grid-template-columns: max-content min-content;
        column-gap: 6px;
        align-items: center;
        .title-text {
            padding-bottom: 5px;
        }
    }
`;

export const ModalTextStyled = styled.p`
    margin: 0;
    color: ${colors.textColorOpacity};
`;

export const ModalPopupHintStyled = styled.div`
    margin-top: 4px;
    ${fonts.textSmall};
    color: rgba(51, 51, 51, 0.5);
`;

export const ModalPopupControlContainer = styled.div`
    margin-top: 24px;

    ${LinkContainerStyled} {
        margin-left: 16px;
    }

    &.center {
        display: flex;
        justify-content: center;
    }

    &.margin-between {
        display: flex;
        & > :not(:last-child) {
            margin-right: 24px;
        }
    }

    &.modal-popup-control__base {
        ${LinkContainerStyled} {
            margin-left: 24px;
        }
    }
`;

export const ModalBodyStyled = styled.div`
    overflow-y: auto;
    max-height: 70vh;

    &.highlightBg {
        background-color: ${colors.backgroundColorModal};

        .drop-down-filter, .multiselect {
            background-color: ${colors.backgroundColorWhite};
        }
    }

    &.highlightBorder {
        border-top: 1px solid ${colors.borderColorSeparate};
        border-bottom: 1px solid ${colors.borderColorSeparate};
    }

    ::-webkit-scrollbar {
        width: 12px;
    }

    ::-webkit-scrollbar-track-piece:vertical {
        background-color: ${colors.backgroundColorDisabled};
    }

    ::-webkit-scrollbar-thumb:vertical {
        background: #b1b3b6 url(${icons.scrollbarThumbIcon}) no-repeat center;
        border-radius: 6px;
    }
`;

export const ModalPopupContainerStyled = styled.div<IModalPopupContainerStyled>`
    width: ${props => props.width ? props.width : '600px'};
    box-sizing: border-box;
    padding: 56px;
    background: ${colors.backgroundColorWhite};

    &.modal-popup-container_without-padding {
        padding-left: 0;
        padding-right: 0;

        ${ModalPopupControlContainer},
        ${ModalPopupTitleStyled},
        ${ModalBodyStyled} {
            padding-left: 56px;
            padding-right: 56px;
        }
    }
`;

export const ModalSeparatorStyled = styled.hr<IModalSeparatorStyled>`
    width: auto;
    border: 0;
    border-top: 1px solid ${colors.borderColorLight};
    margin: ${props =>  `0 -${props.margin}`};
`;
